import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Shield, Zap, Globe, Brain, Mic, QrCode, Eye, EyeOff, Loader } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import BoltBadge from '../components/BoltBadge'
import toast from 'react-hot-toast'

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'merchant' as 'developer' | 'merchant',
    company: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const { signIn, signUp } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isLoading) return
    
    // Validation côté client
    if (!formData.email || !formData.password) {
      toast.error('Email et mot de passe requis')
      return
    }

    if (!isLogin && (!formData.name || formData.name.trim().length < 2)) {
      toast.error('Le nom doit contenir au moins 2 caractères')
      return
    }

    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères')
      return
    }

    try {
      setIsLoading(true)
      
      if (isLogin) {
        await signIn(formData.email, formData.password)
      } else {
        await signUp(formData.email, formData.password, {
          name: formData.name,
          role: formData.role,
          company: formData.company
        })
      }
    } catch (error: any) {
      console.error('Erreur auth:', error)
      if (error.message?.includes('Invalid login credentials')) {
        toast.error('Email ou mot de passe incorrect')
      } else if (error.message?.includes('User already registered')) {
        toast.error('Un compte existe déjà avec cet email')
      } else {
        toast.error(error.message || 'Erreur lors de l\'authentification')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleQuickLogin = async (role: 'admin' | 'developer' | 'merchant') => {
    if (isLoading) return
    
    let email, password = 'PayFuseBolt'
    
    switch (role) {
      case 'admin':
        email = 'admin@payfuse.dev'
        break
      case 'developer':
        email = 'developer@payfuse.dev'
        break
      case 'merchant':
        email = 'merchant@payfuse.dev'
        break
    }
    
    try {
      setIsLoading(true)
      await signIn(email, password)
    } catch (error: any) {
      console.error('Erreur connexion rapide:', error)
      toast.error(error.message || 'Erreur lors de la connexion rapide')
    } finally {
      setIsLoading(false)
    }
  }

  const features = [
    { icon: Shield, title: 'Sécurité Avancée', desc: 'JWT + RBAC + Webhooks signés' },
    { icon: Zap, title: 'API Unifiée', desc: 'REST + GraphQL pour tous les providers' },
    { icon: Globe, title: 'Multi-Provider', desc: 'Orange Money, Wave, MTN MoMo' },
    { icon: Brain, title: 'Assistant IA', desc: 'Génération de code et documentation' },
    { icon: Mic, title: 'Commandes Vocales', desc: 'Interface vocale avec ElevenLabs' },
    { icon: QrCode, title: 'QR Payments', desc: 'Paiements instantanés par QR Code' }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4 relative">
      <BoltBadge variant="light" position="top-right" size="md" />
      
      <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding & Features */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="text-white space-y-8"
        >
          <div>
            <motion.h1 
              className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              PayFuse
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-300 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Passerelle intelligente Mobile Money avec assistant IA intégré
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20"
              >
                <feature.icon className="w-8 h-8 text-blue-400 mb-2" />
                <h3 className="font-semibold mb-1">{feature.title}</h3>
                <p className="text-sm text-gray-300">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Right Side - Auth Form */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-2xl p-8"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {isLogin ? 'Connexion' : 'Inscription'}
            </h2>
            <p className="text-gray-600">
              {isLogin ? 'Accédez à votre tableau de bord PayFuse' : 'Créez votre compte PayFuse'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="John Doe"
                    required={!isLogin}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de compte
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as 'developer' | 'merchant' }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    disabled={isLoading}
                  >
                    <option value="merchant">Commerçant</option>
                    <option value="developer">Développeur</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entreprise (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="Mon Entreprise"
                    disabled={isLoading}
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="email@example.com"
                required
                disabled={isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={isLoading}
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Chargement...
                </>
              ) : (
                isLogin ? 'Se connecter' : 'Créer le compte'
              )}
            </motion.button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              disabled={isLoading}
            >
              {isLogin ? 'Pas de compte ? Inscrivez-vous' : 'Déjà un compte ? Connectez-vous'}
            </button>
          </div>

          {isLogin && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">Connexion rapide:</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    onClick={() => handleQuickLogin('admin')}
                    disabled={isLoading}
                    className="bg-red-100 hover:bg-red-200 text-red-800 px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                  >
                    Admin
                  </button>
                  <button
                    onClick={() => handleQuickLogin('developer')}
                    disabled={isLoading}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-800 px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                  >
                    Développeur
                  </button>
                  <button
                    onClick={() => handleQuickLogin('merchant')}
                    disabled={isLoading}
                    className="bg-green-100 hover:bg-green-200 text-green-800 px-4 py-2 rounded-lg text-sm transition-colors disabled:opacity-50"
                  >
                    Commerçant
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center text-sm text-gray-600">
              <div className="flex justify-center space-x-4 text-xs">
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded">✓ API Unifiée</span>
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">✓ Assistant IA</span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">✓ Temps Réel</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default LoginPage